import React from 'react';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { PieChart as PieChartIcon } from 'lucide-react';

export default function AllocationCharts({ summary }) {
  const formatEuro = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-surface p-3.5 rounded-xl shadow-hp-lg border border-workspace text-xs">
          <p className="font-bold text-primary mb-1">{data.name}</p>
          <p className="text-secondary font-extrabold text-sm">{formatEuro(data.value)}</p>
          <p className="text-subtle mt-1">Part actuelle : <strong className="text-primary">{data.pct}%</strong> (Cible : {data.targetPct}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Graphique Donut */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="lg:col-span-6 workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm flex flex-col items-center justify-between min-h-[380px]"
      >
        <div className="w-full flex items-center justify-between pb-3 border-b border-workspace mb-2">
          <h3 className="text-base font-bold text-primary flex items-center gap-2">
            <PieChartIcon className="w-4 h-4 text-secondary" />
            Répartition Actuelle par Classe d'Actifs
          </h3>
          <span className="text-xs text-subtle font-medium">{summary.chartData.length} classes actives</span>
        </div>

        {summary.chartData.length > 0 ? (
          <div className="w-full h-[280px] relative flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={summary.chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={105}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {summary.chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} stroke="var(--hp-color-surface)" strokeWidth={3} />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            {/* Centre du Donut */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-xs text-subtle font-medium">Total Actifs</span>
              <span className="text-sm font-black text-primary">{formatEuro(summary.totalActifsBruts)}</span>
            </div>
          </div>
        ) : (
          <div className="py-20 text-center text-xs text-subtle italic">
            Aucun actif renseigné pour générer le graphique.
          </div>
        )}
      </motion.div>

      {/* Tableau détaillé des classes d'actifs et écarts */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="lg:col-span-6 workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm flex flex-col justify-between"
      >
        <div className="w-full flex items-center justify-between pb-3 border-b border-workspace mb-4">
          <h3 className="text-base font-bold text-primary">Ventilation & Pondérations</h3>
          <span className="text-xs text-subtle font-medium">Profil : {summary.targetConfig.label}</span>
        </div>

        <div className="space-y-4 flex-1">
          {summary.chartData.map((item, idx) => {
            const gap = summary.gapPercentages[
              item.name.toLowerCase().includes("liquid") ? "liquidites" :
              item.name.toLowerCase().includes("placement") ? "placements" :
              item.name.toLowerCase().includes("immo") ? "immobilier" : "alternatif"
            ] || 0;

            const isOver = gap > 2;
            const isUnder = gap < -2;

            return (
              <div key={idx} className="p-3.5 rounded-xl bg-surface-muted/30 border border-workspace flex flex-col gap-2">
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="font-bold text-primary">{item.name}</span>
                  </div>
                  <span className="font-extrabold text-primary">{formatEuro(item.value)}</span>
                </div>

                {/* Barre de progression */}
                <div className="w-full bg-workspace h-2 rounded-full overflow-hidden flex">
                  <div 
                    className="h-full rounded-full transition-all duration-500" 
                    style={{ width: `${Math.min(100, item.pct)}%`, backgroundColor: item.color }} 
                  />
                </div>

                <div className="flex items-center justify-between text-[11px] text-subtle pt-0.5">
                  <span>Pondération actuelle : <strong className="text-primary">{item.pct}%</strong></span>
                  <span>Cible : <strong>{item.targetPct}%</strong></span>
                  <span className={`font-semibold ${isOver ? 'text-warning-600' : isUnder ? 'text-info-600' : 'text-success-600'}`}>
                    {gap > 0 ? `+${gap}%` : `${gap}%`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

